import { type ProductAutomation } from "@fitness-app/data-models/entities/Automation";
import { getError } from "@fitness-app/utils";

import { type AppThunk } from "../../../index";
import {
  subscribeToProductAutomationFailed,
  subscribeToProductAutomationStarted,
  subscribeToProductAutomationSuccess,
  unsubscribeFromProductAutomation,
  updateProductAutomation,
} from "../reducer";
import { fetchProductAutomation } from "./fetchProductAutomation";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToProductAutomation =
  (productId: string): AppThunk =>
  async (dispatch, getState, { subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "productAutomation");

    try {
      dispatch(subscribeToProductAutomationStarted());

      void dispatch(fetchProductAutomation(productId));

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "productAutomation", claimedSlot)) {
        return;
      }

      const channelName = `product-automation-${productId}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeProductAutomationChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "productAutomation", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.productAutomationChannelName = channelName;
      subscriptions.realtimeProductAutomationChannel = channel;

      subscriptions.realtimeProductAutomationChannel
        .on<ProductAutomation>(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "product_automation", filter: `productId=eq.${productId}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductAutomation(payload.new));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToProductAutomationSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromProductAutomation());
          }
        });
    } catch (e) {
      dispatch(subscribeToProductAutomationFailed(getError(e)));
    }
  };
